import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { API_BASE_URL } from "src/config/apiBase";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import { decryptToken } from "../utils/getToken";

const CrmIntegrationsPanel = () => {
  const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");
  const url = API_BASE_URL;
  const token = decryptToken("accessToken") || "";
  const tenantId = isLoggedIn?.main_id;
  const isStaff = String(isLoggedIn?.type ?? "").toLowerCase() === "staff";

  const [googleHooks, setGoogleHooks] = useState([]);
  const [metaHooks, setMetaHooks] = useState([]);
  const [loading, setLoading] = useState(true);

  const backendHint = url.replace(/\/$/, "");

  const loadHooks = useCallback(async () => {
    if (!tenantId || isStaff) return;
    setLoading(true);
    try {
      const [gRes, mRes] = await Promise.all([
        fetch(`${url}/google-ads/${tenantId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${url}/meta-ads/${tenantId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      const gData = gRes.ok ? await gRes.json() : { data: [] };
      const mData = mRes.ok ? await mRes.json() : { data: [] };
      setGoogleHooks(gData.data || []);
      setMetaHooks(mData.data || []);
    } catch {
      toast.error("Failed to load integrations");
    } finally {
      setLoading(false);
    }
  }, [tenantId, isStaff, url, token]);

  useEffect(() => {
    loadHooks();
  }, [loadHooks]);

  const createHook = async (provider) => {
    const title = window.prompt(`Name for this ${provider} integration:`);
    if (!title?.trim()) return;
    try {
      const keyRes = await fetch(`${url}/${provider}-ads/generate-key`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!keyRes.ok) throw new Error("Could not generate key");
      const { webhook_key } = await keyRes.json();
      const createRes = await fetch(`${url}/${provider}-ads`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          webhook_key,
          title: title.trim(),
          main_id: tenantId,
          main_name: isLoggedIn?.full_name || "",
        }),
      });
      if (!createRes.ok) throw new Error("Create failed");
      toast.success(`${provider === "google" ? "Google" : "Meta"} webhook created`);
      loadHooks();
    } catch (e) {
      toast.error(e.message || "Failed to create webhook");
    }
  };

  const copyText = (text) => {
    navigator.clipboard?.writeText(text);
    toast.info("Copied to clipboard");
  };

  if (isStaff) return null;

  const googleWebhookUrl = `${backendHint}/leads/integrations/google-ads-leads`;
  const metaWebhookUrl = `${backendHint}/leads/integrations/meta-leads`;

  return (
    <div className="crm-integrations">
      <h3 className="crm-integrations__title">Lead integrations</h3>
      {loading ? (
        <p className="crm-insights__muted">Loading…</p>
      ) : (
        <>
          <section className="crm-integrations__block">
            <div className="crm-integrations__head">
              <strong>Google Ads</strong>
              <button type="button" onClick={() => createHook("google")}>
                + Add webhook
              </button>
            </div>
            <p className="crm-integrations__url">
              POST <code>{googleWebhookUrl}</code>
              <button type="button" onClick={() => copyText(googleWebhookUrl)}>Copy</button>
            </p>
            {googleHooks.length === 0 ? (
              <p className="crm-insights__muted">No Google Ads webhooks yet.</p>
            ) : (
              googleHooks.map((h) => (
                <div key={h.id} className="crm-integrations__row">
                  <span>{h.title}</span>
                  <code>{h.webhook_key}</code>
                  <button type="button" onClick={() => copyText(h.webhook_key)}>Copy key</button>
                </div>
              ))
            )}
          </section>

          <section className="crm-integrations__block">
            <div className="crm-integrations__head">
              <strong>Meta Lead Ads</strong>
              <button type="button" onClick={() => createHook("meta")}>
                + Add webhook
              </button>
            </div>
            <p className="crm-integrations__url">
              POST <code>{metaWebhookUrl}</code>
              <button type="button" onClick={() => copyText(metaWebhookUrl)}>Copy</button>
            </p>
            <p className="crm-insights__muted" style={{ fontSize: "12px" }}>
              Send <code>meta_key</code> (or header <code>x-meta-webhook-key</code>) plus{" "}
              <code>full_name</code>, <code>phone_number</code>, <code>email</code> — or Meta{" "}
              <code>field_data</code> array.
            </p>
            {metaHooks.length === 0 ? (
              <p className="crm-insights__muted">No Meta webhooks yet.</p>
            ) : (
              metaHooks.map((h) => (
                <div key={h.id} className="crm-integrations__row">
                  <span>{h.title}</span>
                  <code>{h.webhook_key}</code>
                  <button type="button" onClick={() => copyText(h.webhook_key)}>Copy key</button>
                </div>
              ))
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default CrmIntegrationsPanel;
